import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';

// A card over a tile that a tap on it opens to change something: the thermostat's set point
// (tiles.md §5) and a status tile's mode (status-tile.md ST-5). In the chrome's look; the page is
// dimmed behind it. Like the app's dialogs, it closes only by its own buttons or Escape (issue #16),
// not by a tap beside it.
//
// The card is on the page (a portal), but React still passes its events to the tile, which would
// open it again: every click and press stops here.

/** The chrome's colours (the app's `ui/theme.ts`, look LK-9), which this package cannot import. */
export const CARD_COLORS = { panel: '#202020', raised: '#2e2e2e', accent: '#2563eb', muted: '#a0a0a0' } as const;
const RADIUS = 12;
/** The chrome's font (the app's Chakra theme); the page itself sets none. */
const FONT = 'system-ui, sans-serif';
const WIDTH = 300;
const MARGIN = 12;

export const stopEvent = (event: { stopPropagation() : void }) : void => event.stopPropagation();

/** A button that does not start a text selection or a long-press menu on a touch screen. */
export const CARD_BUTTON: CSSProperties = {
    border: 'none',
    color: '#ffffff',
    cursor: 'pointer',
    font: 'inherit',
    touchAction: 'manipulation',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    WebkitTouchCallout: 'none',
    WebkitTapHighlightColor: 'transparent',
} as CSSProperties;

export interface PickerCardProps {
    /** The tile's place on the page. */
    anchor: DOMRect;
    /** The tile's label, at the top. */
    title: string;
    /** Beside the title, muted: the room's temperature, for example. */
    aside?: string | undefined;
    /** What it changes, for a screen reader: "set point". */
    label: string;
    /** Takes the focus once the card is shown (a hidden element cannot). */
    initialFocus?: RefObject<HTMLElement | null> | undefined;
    onClose() : void;
    children: ReactNode;
}

export function PickerCard({ anchor, title, aside, label, initialFocus, onClose, children }: PickerCardProps) : ReactNode {
    const card = useRef<HTMLDivElement>(null);
    const [top, setTop] = useState<number | null>(null);

    // Over the tile's middle, kept inside the window, and below the status bar or title bar
    // (`--casa-top-inset`, a CSS value only the page resolves, so the clamp is CSS; the Mac's
    // status tile results, note 1); placed once its height is known.
    const width = Math.min(WIDTH, window.innerWidth - 2 * MARGIN);
    const left = Math.min(Math.max(MARGIN, anchor.left + anchor.width / 2 - width / 2), window.innerWidth - width - MARGIN);
    useLayoutEffect(() => {
        const height = card.current?.offsetHeight ?? 0;
        setTop(Math.min(Math.max(MARGIN, anchor.top + anchor.height / 2 - height / 2), window.innerHeight - height - MARGIN));
    }, [anchor]);
    const isShown = top !== null;
    useEffect(() => {
        if (isShown) {
            initialFocus?.current?.focus({ preventScroll: true });
        }
    }, [isShown, initialFocus]);

    // Escape closes, wherever the focus is.
    const latestClose = useRef(onClose);
    latestClose.current = onClose;
    useEffect(() => {
        const onKey = (event: KeyboardEvent) : void => {
            if (event.key === 'Escape') {
                event.stopPropagation();
                latestClose.current();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    return createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0, 0, 0, 0.55)' }} onClick={stopEvent} onPointerDown={stopEvent}>
            <div
                ref={card}
                role="dialog"
                aria-modal="true"
                aria-label={`${title}: ${label}`}
                style={{
                    position: 'fixed',
                    left,
                    top: top === null ? anchor.top : `max(calc(var(--casa-top-inset, 0px) + ${MARGIN}px), ${top}px)`,
                    visibility: isShown ? 'visible' : 'hidden',
                    width,
                    maxHeight: `calc(${window.innerHeight - 2 * MARGIN}px - var(--casa-top-inset, 0px))`,
                    overflowY: 'auto',
                    boxSizing: 'border-box',
                    padding: 16,
                    background: CARD_COLORS.panel,
                    color: '#ffffff',
                    fontFamily: FONT,
                    borderRadius: RADIUS,
                    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.6)',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0, fontWeight: 600, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
                    {aside ? <div style={{ flex: 'none', fontSize: 14, color: CARD_COLORS.muted }}>{aside}</div> : null}
                </div>
                {children}
            </div>
        </div>,
        document.body,
    );
}

/** A full-width button at the card's bottom: Cancel, or the main one (Set) in the accent colour. */
export function CardButton({ label, isMain = false, buttonRef, onPress }: { label: string; isMain?: boolean; buttonRef?: RefObject<HTMLButtonElement | null>; onPress() : void }) : ReactNode {
    return (
        <button
            ref={buttonRef}
            type="button"
            onClick={(event) => {
                stopEvent(event);
                onPress();
            }}
            style={{ ...CARD_BUTTON, flex: 1, height: 44, borderRadius: 10, background: isMain ? CARD_COLORS.accent : CARD_COLORS.raised, fontSize: 16, fontWeight: isMain ? 600 : 400 }}
        >
            {label}
        </button>
    );
}
